import api from 'api';
import {
    PROFILE_SECTION_KEYS,
    areAllCompletedFormFieldsTrue,
    isCompletedAssessmentUnset,
} from 'hooks/useCompletedForms';

/** Prevents repeat POSTs in this SPA session after a successful stamp. */
const stampedAssessmentUserIds = new Set<number>();

export interface StampCompletedAssessmentOptions {
    userId: number;
    completedAssessment?: string | Date | null;
    /** Keys just PATCHed to true (overlaid so a stale GET cannot miss them). */
    patched?: Record<string, boolean>;
    /**
     * Completion keys that must be true. Defaults to profile sections so extra
     * completed-forms fields such as `purpose` do not block the stamp.
     */
    requiredKeys?: readonly string[];
}

function readUserRecord(data: unknown): { id?: unknown; completed_assessment?: unknown } | null {
    if (Array.isArray(data)) {
        const first = data[0];
        if (first && typeof first === 'object') {
            return first as { id?: unknown; completed_assessment?: unknown };
        }
        return null;
    }
    if (data && typeof data === 'object') {
        return data as { id?: unknown; completed_assessment?: unknown };
    }
    return null;
}

/**
 * POST /candidate_info/:id/user/completed-assessment when every required
 * completed-forms field is true and completed_assessment is still unset.
 * Does not navigate.
 */
export async function stampCompletedAssessmentIfReady({
    userId,
    completedAssessment,
    patched,
    requiredKeys = PROFILE_SECTION_KEYS,
}: StampCompletedAssessmentOptions): Promise<boolean> {
    if (!Number.isFinite(userId) || userId <= 0) {
        return false;
    }
    if (stampedAssessmentUserIds.has(userId)) {
        return false;
    }

    try {
        const resp = await api.completedForms.getCompletedForms(userId);
        const forms: Record<string, unknown> = {
            ...((resp?.data ?? {}) as Record<string, unknown>),
            ...patched,
        };
        if (!areAllCompletedFormFieldsTrue(forms, requiredKeys)) {
            return false;
        }

        let assessment: unknown = completedAssessment;
        if (isCompletedAssessmentUnset(assessment)) {
            try {
                const userResp = await api.tableInput.getCandidateInfo(String(userId));
                const record = readUserRecord(userResp?.data);
                if (record && (record.id == null || Number(record.id) === userId)) {
                    assessment = record.completed_assessment;
                }
            } catch {
                // Fall through and stamp from the provided (unset) value.
            }
        }
        if (!isCompletedAssessmentUnset(assessment)) {
            return false;
        }

        await api.tableInput.saveAssessmentStatus(userId);
        stampedAssessmentUserIds.add(userId);
        return true;
    } catch (err) {
        console.error('Failed to stamp completed assessment', err);
        return false;
    }
}

export function requiredProfileFormKeys(hasSpouse: boolean): readonly string[] {
    if (hasSpouse) {
        return PROFILE_SECTION_KEYS;
    }
    return PROFILE_SECTION_KEYS.filter((key) => key !== 'spouse');
}
